// src/components/admin/RecentActivityWidget.js
import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tabs,
  Tab,
  Divider,
  Alert,
  Button,
  Tooltip
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Event as EventIcon,
  Payment as PaymentIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Security as SecurityIcon,
  Settings as SettingsIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  History as HistoryIcon,
  Warning as WarningIcon,
  CheckCircle as SuccessIcon,
  Info as InfoIcon,
  Error as ErrorIcon
} from '@mui/icons-material';

const RecentActivityWidget = ({ data }) => {
  const [tabValue, setTabValue] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState(null);

  if (!data || !Array.isArray(data)) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardHeader title="📋 Actividad Reciente" />
        <CardContent>
          <Typography color="text.secondary">
            No hay actividad reciente para mostrar
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const getActivityIcon = (type) => {
    const icons = {
      user_registered: <PersonAddIcon />,
      user_login: <PersonAddIcon />,
      user_logout: <PersonAddIcon />,
      event_created: <EventIcon />,
      event_updated: <EditIcon />,
      event_deleted: <DeleteIcon />,
      payment_received: <PaymentIcon />,
      payment_failed: <ErrorIcon />,
      settings_changed: <SettingsIcon />,
      security_alert: <SecurityIcon />,
      system_backup: <HistoryIcon />,
      system_error: <ErrorIcon />
    };
    return icons[type] || <InfoIcon />;
  };

  const getActivityColor = (type) => {
    const colors = {
      user_registered: 'success',
      user_login: 'info',
      user_logout: 'default',
      event_created: 'primary',
      event_updated: 'warning',
      event_deleted: 'error',
      payment_received: 'success',
      payment_failed: 'error',
      settings_changed: 'warning',
      security_alert: 'error',
      system_backup: 'info',
      system_error: 'error'
    };
    return colors[type] || 'default';
  };

  const getActivityTitle = (type) => {
    const titles = {
      user_registered: 'Usuario Registrado',
      user_login: 'Inicio de Sesión',
      user_logout: 'Cierre de Sesión',
      event_created: 'Evento Creado',
      event_updated: 'Evento Actualizado',
      event_deleted: 'Evento Eliminado',
      payment_received: 'Pago Recibido',
      payment_failed: 'Pago Fallido',
      settings_changed: 'Configuración Cambiada',
      security_alert: 'Alerta de Seguridad',
      system_backup: 'Backup del Sistema',
      system_error: 'Error del Sistema'
    };
    return titles[type] || 'Actividad';
  };

  const getPriorityLevel = (type) => {
    const priorities = {
      security_alert: 'high',
      payment_failed: 'high',
      system_error: 'high',
      event_deleted: 'medium',
      settings_changed: 'medium',
      user_registered: 'low',
      event_created: 'low',
      payment_received: 'low'
    };
    return priorities[type] || 'low';
  };

  // Filtrar actividades por prioridad para las pestañas
  const allActivities = data || [];
  const highPriorityActivities = allActivities.filter(a => getPriorityLevel(a.type) === 'high');
  const systemActivities = allActivities.filter(a => 
    ['system_backup', 'system_error', 'settings_changed', 'security_alert'].includes(a.type)
  );

  const getCurrentActivities = () => {
    switch (tabValue) {
      case 0: return allActivities.slice(0, 10);
      case 1: return highPriorityActivities.slice(0, 10);
      case 2: return systemActivities.slice(0, 10);
      default: return allActivities.slice(0, 10);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleMenuOpen = (event, activity) => {
    setAnchorEl(event.currentTarget);
    setSelectedActivity(activity);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedActivity(null);
  };

  const handleActivityAction = (action) => {
    console.log(`${action} actividad:`, selectedActivity);
    handleMenuClose();
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Ahora mismo';
    if (diffInMinutes < 60) return `Hace ${diffInMinutes}m`;
    if (diffInMinutes < 1440) return `Hace ${Math.floor(diffInMinutes / 60)}h`;
    return date.toLocaleDateString('es-ES', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const currentActivities = getCurrentActivities();

  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader
        title="📋 Actividad Reciente"
        subheader={`${allActivities.length} eventos registrados`}
        action={
          <Box display="flex" gap={1}>
            <Tooltip title="Actualizar actividad">
              <IconButton size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Filtros avanzados">
              <IconButton size="small">
                <FilterIcon />
              </IconButton>
            </Tooltip>
          </Box>
        }
      />
      
      <CardContent>
        {/* Alertas de alta prioridad */}
        {highPriorityActivities.length > 0 && (
          <Alert 
            severity="warning" 
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small">
                Ver Todo
              </Button>
            }
          >
            {highPriorityActivities.length} evento(s) de alta prioridad requieren atención
          </Alert>
        )}

        {/* Tabs para diferentes vistas */}
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab 
            label={`Todas (${allActivities.length})`} 
            sx={{ fontSize: '0.75rem' }}
          />
          <Tab 
            label={`Críticas (${highPriorityActivities.length})`} 
            sx={{ fontSize: '0.75rem' }}
          />
          <Tab 
            label={`Sistema (${systemActivities.length})`} 
            sx={{ fontSize: '0.75rem' }}
          />
        </Tabs>

        {/* Lista de actividades */}
        <List dense sx={{ maxHeight: 400, overflow: 'auto' }}>
          {currentActivities.map((activity, index) => (
            <React.Fragment key={activity.id || index}>
              <ListItem
                alignItems="flex-start"
                secondaryAction={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="caption" color="text.secondary">
                      {formatTimestamp(activity.timestamp)}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, activity)}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>
                }
              >
                <ListItemAvatar>
                  <Avatar 
                    sx={{ 
                      bgcolor: `${getActivityColor(activity.type)}.main`,
                      width: 32,
                      height: 32
                    }}
                  >
                    {React.cloneElement(getActivityIcon(activity.type), { fontSize: 'small' })}
                  </Avatar>
                </ListItemAvatar>
                
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" fontWeight="bold">
                        {getActivityTitle(activity.type)}
                      </Typography>
                      {getPriorityLevel(activity.type) === 'high' && (
                        <Chip 
                          label="Alta" 
                          color="error" 
                          size="small"
                          sx={{ fontSize: '0.6rem', height: 16 }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.primary">
                        {activity.description}
                      </Typography>
                      {activity.metadata && (
                        <Typography variant="caption" color="text.secondary">
                          {typeof activity.metadata === 'object' 
                            ? Object.entries(activity.metadata).map(([key, value]) => 
                                `${key}: ${value}`
                              ).join(' • ')
                            : activity.metadata
                          }
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </ListItem>
              
              {index < currentActivities.length - 1 && <Divider variant="inset" component="li" />}
            </React.Fragment>
          ))}
        </List>

        {/* Menu contextual */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => handleActivityAction('view')}>
            <InfoIcon sx={{ mr: 1 }} fontSize="small" />
            Ver Detalles
          </MenuItem>
          <MenuItem onClick={() => handleActivityAction('investigate')}>
            <HistoryIcon sx={{ mr: 1 }} fontSize="small" />
            Investigar
          </MenuItem>
          {getPriorityLevel(selectedActivity?.type) === 'high' && (
            <MenuItem onClick={() => handleActivityAction('resolve')}>
              <SuccessIcon sx={{ mr: 1 }} fontSize="small" />
              Marcar como Resuelto
            </MenuItem>
          )}
        </Menu>

        {/* Estadísticas rápidas */}
        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" color="text.secondary">
              📊 Última hora: {allActivities.filter(a => {
                const hour = 60 * 60 * 1000;
                return new Date(a.timestamp) > new Date(Date.now() - hour);
              }).length} eventos
            </Typography>
            
            <Button 
              size="small" 
              variant="text"
              onClick={() => console.log('Ver historial completo')}
            >
              Ver Historial
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default RecentActivityWidget;